// The job's price: home size, the priced note, reprice lines.
import {
  type HomeAnswers,
  homeSize,
  type JobDetail,
  type JobPatch,
  type LineInput,
  type LineRow,
  PRICE_OFTEN,
  PRICE_OFTEN_WORDS,
  type PriceList,
  priceMath,
  type PropertyRow,
  type QuickQuote,
} from "@steward/shared";
import { useState } from "react";
import { useSaveProperty, useUpdateJob, useVisitLines } from "../../api/hooks";
import { useSession } from "../../app/session";
import { InputCount, InputText } from "../../components/fields";
import { moneyExact, shortDate, todayIn } from "../../components/format";
import { Modal, useToast } from "../../components/overlays";
import { Banner, Button, Switch } from "../../components/parts";
import { errText } from "../../components/work";
import { QuickPrice } from "../QuickPrice";

const oftenWords = (o: keyof typeof PRICE_OFTEN_WORDS) => PRICE_OFTEN_WORDS[o].toLowerCase();

/** The first line whose name holds a price list service ("Deep clean" is Deep clean), longest name first, with it. */
function handLine(job: JobDetail, list: PriceList): { line: LineRow; serviceId: string } | null {
  const services = [...list.services].sort((x, y) => y.name.length - x.name.length);
  for (const l of job.lines) {
    const d = l.description.toLowerCase();
    const s =
      services.find((x) => d.includes(x.name.toLowerCase())) ??
      services.find((x) => {
        const word = x.name.toLowerCase().split(/\s+/)[0] ?? "";
        return word.length > 3 && d.startsWith(word);
      });
    if (s) return { line: l, serviceId: s.id };
  }
  return null;
}

/**
 * The home's size, and the line priced from it (owner, 2026-11-02: "edit how many bedrooms & baths a line item should
 * have in case we let it slide after the cleaning was done"). The line takes the real size and keeps its price, so it
 * reads as changed by hand, and a plan is priced from the real size.
 */
export function HomeSizeEdit({
  place,
  clientId,
  job,
  prices,
  onClose,
}: {
  place: PropertyRow;
  clientId: string;
  job: JobDetail;
  prices: PriceList | undefined;
  onClose: () => void;
}) {
  const { company } = useSession();
  const save = useSaveProperty();
  const update = useUpdateJob();
  const toast = useToast();
  const priced0 = job.lines.find((l) => l.answers);
  // A line priced by hand (an old website quote, or typed in) links to its service on the price list by name, so it
  // shows the real-size math, the plan prices under it and Let it pass too (owner, 2026-11-02).
  const hand = !priced0 && prices ? handLine(job, prices) : null;
  const line = priced0 ?? hand?.line ?? null;
  const base: HomeAnswers | null =
    priced0?.answers ??
    (hand
      ? {
          serviceId: hand.serviceId,
          sizeBy: "rooms",
          beds: 0,
          baths: 0,
          other: 0,
          sqft: 0,
          often: (PRICE_OFTEN as readonly string[]).includes(job.frequency) ? (job.frequency as HomeAnswers["often"]) : "once",
          extras: {},
        }
      : null);
  const serviceName = prices?.services.find((x) => x.id === base?.serviceId)?.name ?? "price list";
  // Let it pass (owner, 2026-11-02): keep this clean's price; the plan prices under the line come from the real size.
  const [letPass, setLetPass] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const start = (n: number | null) => (n === null ? "" : String(n));
  // A home with no size saved starts from what its line was priced for.
  const a = priced0?.answers;
  const blank = place.beds === null && place.baths === null && place.rooms === null && place.sqft === null;
  const [f, setF] = useState({
    beds: start(blank && a?.sizeBy === "rooms" ? a.beds || null : place.beds),
    baths: start(blank && a?.sizeBy === "rooms" ? a.baths || null : place.baths),
    rooms: start(blank && a?.sizeBy === "rooms" ? a.other || null : place.rooms),
    sqft: start(blank && a?.sizeBy === "sqft" ? a.sqft || null : place.sqft),
  });
  const n = (s: string) => (s.trim() && Number.isFinite(Number(s)) ? Number(s) : null);
  const count = (k: "beds" | "baths" | "rooms") => ({ value: f[k], onChange: (v: string) => setF({ ...f, [k]: v }) });
  const submit = async () => {
    setError(null);
    const sqft = n(f.sqft);
    try {
      await save.mutateAsync({
        clientId,
        id: place.id,
        property: {
          address: place.address,
          beds: n(f.beds),
          baths: n(f.baths),
          rooms: n(f.rooms),
          sqft: sqft === null ? null : Math.round(sqft),
        },
      });
      if (line && base) {
        // The priced line takes the real size and keeps its price (they let it slide); from today on, so visits
        // already done keep what they billed.
        const beds = n(f.beds) ?? 0;
        const baths = n(f.baths) ?? 0;
        const other = n(f.rooms) ?? 0;
        const feet = sqft === null ? 0 : Math.round(sqft);
        const bySqft = base.sizeBy === "sqft" ? feet > 0 : feet > 0 && beds + baths === 0;
        const sized: HomeAnswers = { ...base, sizeBy: bySqft ? "sqft" : "rooms", beds, baths, other, sqft: feet };
        // Not let pass: the line takes the price list's price for the real size (from today on).
        const priced = !letPass && prices ? priceMath(prices, sized) : null;
        const answers: HomeAnswers = { ...sized, letPass: letPass || undefined };
        const was = line.basis ? `, ${line.basis}` : null;
        const now = homeSize(answers, { sqft: bySqft ? feet : 0 });
        const lines: LineInput[] = job.lines.map((l) => ({
          description:
            l.id === line.id && was && l.description.endsWith(was)
              ? `${l.description.slice(0, -was.length)}, ${now}`
              : l.description,
          note: l.note,
          link: l.link ?? null,
          quantity: l.quantity,
          unitPriceCents: l.id === line.id && priced ? priced.cents : l.unitPriceCents,
          frequency: l.frequency as LineInput["frequency"],
          ...(l.id === line.id ? { answers } : l.answers ? { answers: l.answers } : {}),
        }));
        await update.mutateAsync({ id: job.id, patch: { lines, linesFrom: todayIn(company.timezone) } });
      }
      toast(
        !line || !base
          ? "Home size saved"
          : letPass
            ? "Home size saved. Same price, plans from the real size."
            : "Home size saved and priced",
      );
      onClose();
    } catch (err) {
      setError(errText(err));
    }
  };
  return (
    <Modal
      title="Home size"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending || update.isPending} onClick={() => void submit()}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">The crew see this on the job and the visit. The client sees it on their quote and bill.</p>
        {line && base ? (
          <label className="row">
            <Switch checked={letPass} onChange={setLetPass} label={`Let it pass at ${moneyExact(line.unitPriceCents)}`} />
            <span className="small">
              {/* Plans never come from this line's service: they use the recurring prices (owner, 2026-11-02). */}
              {letPass
                ? `Let it pass. This clean stays ${moneyExact(line.unitPriceCents)} (${serviceName} price). Plans use your recurring prices for the real size.`
                : `Price this clean at the ${serviceName} price for the real size, from today on. Visits already done keep what they billed. Plans use your recurring prices.`}
            </span>
          </label>
        ) : null}
        {error ? <Banner type="error">{error}</Banner> : null}
        <div className="form-grid">
          <InputCount label="Bedrooms" max={20} {...count("beds")} />
          <InputCount label="Bathrooms" max={20} decimal {...count("baths")} />
          <InputCount label="Other rooms" max={50} {...count("rooms")} />
          <InputText
            label="Square feet"
            inputMode="numeric"
            autoComplete="off"
            value={f.sqft}
            onChange={(e) => setF({ ...f, sqft: e.target.value })}
          />
        </div>
      </div>
    </Modal>
  );
}

/**
 * Under a job line priced from the price list: the home, the math and, for a one time price, what this home would cost to
 * repeat (owner, 2026-10-29: "on a line item that used autoquote for it, to underneath show how it got that, with it being
 * 3 beds, 2 baths, 2 other rooms etc").
 */
export function PricedNote({ line, list }: { line: LineRow; list: PriceList | undefined }) {
  if (!line.answers || !list) return null;
  const m = priceMath(list, line.answers);
  if (!m) return <div className="muted small line-basis">Priced from the price list. That service is not on it anymore.</div>;
  const often = m.repeat ? `, ${oftenWords(m.repeat)}` : "";
  const hand =
    m.cents === line.unitPriceCents
      ? ""
      : line.answers.letPass
        ? ` Let it pass at ${moneyExact(line.unitPriceCents)}.`
        : ` Changed by hand to ${moneyExact(line.unitPriceCents)}.`;
  const last = m.repeats.length - 1;
  return (
    <div className="muted small line-basis">
      <div>
        {m.isMin
          ? `Auto quote for ${m.size}. The lowest price${often}, ${moneyExact(m.cents)}.`
          : `Auto quote for ${m.size}. ${m.math}${often} = ${moneyExact(m.cents)}.`}
        {hand}
      </div>
      {m.repeats.length ? (
        <div>
          To repeat,{" "}
          {m.repeats
            .map((r, i) => `${i && i === last ? "or " : ""}${moneyExact(r.perVisitCents)} ${oftenWords(r.often)}`)
            .join(", ")}
          . Open the line to make it repeat.
        </div>
      ) : null}
    </div>
  );
}

/** A price list add-on line ("Inside the fridge", "Inside windows × 3") or a first visit difference line, which pricing
 * again puts back from the new answers. */

const autoLine = (list: PriceList, d: string) =>
  d.endsWith(" on the first visit") || list.extras.some((e) => d === e.name || d.startsWith(`${e.name} × `));

/**
 * The auto quote again, from a job line priced by it: change a room, the service or how often, and the job takes the new
 * lines from today (or its first day, when that is ahead). A one-off made to repeat keeps its start day, so the next visit
 * is one round after it; when the first visit is still ahead it keeps the first visit price and add-ons on that visit.
 */
export function RepriceLine({
  job,
  line,
  list,
  place,
  onClose,
}: {
  job: JobDetail;
  line: LineRow;
  list: PriceList;
  /** The home, whose size starts a line not priced from the list before. */
  place: PropertyRow | null;
  onClose: () => void;
}) {
  const { company } = useSession();
  const toast = useToast();
  const update = useUpdateJob();
  const visitLines = useVisitLines();
  const [error, setError] = useState<string | null>(null);
  const today = todayIn(company.timezone);
  const firstAhead = job.startDate !== null && job.startDate >= today;
  const from = job.startDate && job.startDate > today ? job.startDate : today;
  const use = async (quick: QuickQuote, answers: HomeAnswers) => {
    setError(null);
    const repeat = quick.frequency !== "one-time";
    const kept: LineInput[] = job.lines
      .filter((l) => l.id !== line.id && !l.answers && !autoLine(list, l.description))
      .map((l) => ({
        description: l.description,
        note: l.note,
        link: l.link ?? null,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
        frequency: l.frequency as LineInput["frequency"],
      }));
    const asLine = (l: QuickQuote["lines"][number], first: boolean): LineInput => ({
      description: l.description,
      quantity: 1,
      unitPriceCents: l.unitPriceCents,
      note: l.note ?? null,
      link: l.link ?? null,
      frequency: "every",
      ...(first ? { answers } : {}),
    });
    const lines = [...quick.lines.filter((l) => !repeat || l.recurring).map((l, i) => asLine(l, i === 0)), ...kept];
    const changed = quick.frequency !== job.frequency;
    const patch: JobPatch = {
      lines,
      linesFrom: from,
      ...(changed
        ? {
            frequency: quick.frequency as JobPatch["frequency"] & string,
            // A done deep clean made to repeat is named for the repeat ("Cleaning every two weeks").
            title: firstAhead ? quick.title : (quick.lines[0]?.description.split(",")[0] ?? quick.title),
          }
        : {}),
    };
    try {
      await update.mutateAsync({ id: job.id, patch });
      if (repeat && firstAhead && job.startDate && quick.lines.some((l) => !l.recurring))
        await visitLines.mutateAsync({
          ref: `${job.id}.${job.startDate}`,
          lines: [...quick.lines.map((l, i) => asLine(l, i === 0)), ...kept],
          scope: "this",
        });
      toast(changed && repeat ? `Job repeats ${oftenWords(answers.often)} now.` : "Price updated.");
      onClose();
    } catch (e) {
      setError(errText(e));
    }
  };
  return (
    <Modal title="Price it again" open onClose={onClose}>
      <div className="stack">
        <p className="muted small">
          {job.frequency === "one-time"
            ? "Change the rooms or the service, or pick how often to make it a repeat job. Visits after the first get the repeat price."
            : "Change the rooms, the service or how often."}{" "}
          {from === today ? `It starts today. Visits before keep their price.` : `It starts ${shortDate(from)}.`}
        </p>
        {error ? <Banner type="error">{error}</Banner> : null}
        <QuickPrice
          start={line.answers ?? undefined}
          size={
            line.answers || !place ? undefined : { beds: place.beds, baths: place.baths, rooms: place.rooms, sqft: place.sqft }
          }
          useLabel={update.isPending || visitLines.isPending ? "Saving" : "Update the job"}
          onUse={(q, a) => void use(q, a)}
        />
      </div>
    </Modal>
  );
}
